import React, { useState, useEffect } from 'react';
import { X, Calendar as CalendarIcon, Users, MessageSquare, Send, CheckCircle2 } from 'lucide-react';
import './CheckAvailabilityModal.css';
import PublicAvailabilityCalendar from './PublicAvailabilityCalendar';
import API_BASE_URL from '../config';

interface CheckAvailabilityModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: { fromDate: string; toDate: string; expectedGuests: number; message: string }) => Promise<void>;
    vendorNames: string[];
    vendorIds: string[];
}

const CheckAvailabilityModal: React.FC<CheckAvailabilityModalProps> = ({ 
    isOpen, onClose, onSubmit, vendorNames, vendorIds 
}) => {
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [expectedGuests, setExpectedGuests] = useState(100);
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [availabilityStatus, setAvailabilityStatus] = useState<'idle' | 'checking' | 'available' | 'booked'>('idle');

    // Reset state when vendor changes or modal opens
    useEffect(() => {
        if (isOpen) {
            setFromDate('');
            setToDate('');
            setExpectedGuests(100);
            setMessage('');
            setAvailabilityStatus('idle');
            setSuccess(false);
        }
    }, [isOpen, vendorIds.join(',')]);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const sanitizeVendorId = (id: string) => {
        if (!id) return '';
        if (id.startsWith('prod_')) {
            return id.split('_')[1]; // Format: prod_VENDORID_INDEX
        }
        return id;
    };

    const handleCheckStatus = async () => {
        if (!fromDate || vendorIds.length === 0) return;
        setAvailabilityStatus('checking');
        try {
            const rawId = vendorIds[0];
            const vendorId = sanitizeVendorId(rawId);
            
            const res = await fetch(`${API_BASE_URL}/api/market-vendors/${vendorId}/availability/check?fromDate=${fromDate}&toDate=${toDate || fromDate}`);
            const data = await res.json();
            if (data.success) {
                setAvailabilityStatus(data.isAvailable ? 'available' : 'booked');
            }
        } catch (error) {
            setAvailabilityStatus('idle');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            await onSubmit({ fromDate, toDate: toDate || fromDate, expectedGuests, message });
            setSuccess(true);
            setTimeout(() => {
                setSuccess(false);
                onClose();
            }, 2500);
        } catch (err) {
            alert('Failed to send inquiry. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleDateSelect = (newFrom: string, newTo: string) => {
        setFromDate(newFrom);
        setToDate(newTo);
        setAvailabilityStatus('idle');
    };

    return (
        <div className="availability-modal-overlay">
            <div className="availability-modal-content glass-effect large-modal">
                <button className="modal-close-btn" onClick={onClose}><X /></button>
                
                {success ? (
                    <div className="modal-success-view">
                        <CheckCircle2 size={60} color="#d4af37" />
                        <h2>Request Sent!</h2>
                        <p>The vendors will check their calendar and get back to you shortly.</p>
                    </div>
                ) : (
                    <div className="modal-two-col">
                        <div className="modal-calendar-section">
                            <PublicAvailabilityCalendar 
                                vendorId={vendorIds[0]}
                                selectedFromDate={fromDate}
                                selectedToDate={toDate}
                                onDateSelect={handleDateSelect}
                            />
                        </div>

                        <div className="modal-form-section">
                            <div className="modal-header">
                                <h2>Enquire Now</h2>
                                <p>Checking for: <strong>{vendorNames[0]}</strong></p>
                            </div>

                            <form onSubmit={handleSubmit} className="availability-form">
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.8rem' }}>
                                    <div className="form-group">
                                        <label><CalendarIcon size={14} /> From</label>
                                        <input 
                                            type="date" 
                                            required 
                                            value={fromDate} 
                                            onChange={(e) => setFromDate(e.target.value)} 
                                            min={new Date().toISOString().split('T')[0]}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label><CalendarIcon size={14} /> To</label>
                                        <input 
                                            type="date" 
                                            value={toDate} 
                                            onChange={(e) => setToDate(e.target.value)} 
                                            min={fromDate || new Date().toISOString().split('T')[0]}
                                        />
                                    </div>
                                </div>

                                <button 
                                    type="button" 
                                    className={`check-status-btn-lite ${availabilityStatus}`}
                                    onClick={handleCheckStatus}
                                    disabled={!fromDate || availabilityStatus === 'checking'}
                                >
                                    {availabilityStatus === 'checking' ? 'Verifying...' : 
                                     availabilityStatus === 'available' ? '✓ Available' :
                                     availabilityStatus === 'booked' ? '✕ Booked' : 'Verify Dates'}
                                </button>

                                <div className="form-group">
                                    <label><Users size={14} /> Guests ({expectedGuests})</label>
                                    <input 
                                        type="range" min="50" max="2000" step="50"
                                        value={expectedGuests}
                                        onChange={(e) => setExpectedGuests(parseInt(e.target.value))}
                                    />
                                </div>

                                <div className="form-group">
                                    <label><MessageSquare size={14} /> Requirements</label>
                                    <textarea 
                                        placeholder="Specific requests..."
                                        value={message}
                                        onChange={(e) => setMessage(e.target.value)}
                                        rows={3}
                                    />
                                </div>

                                <button type="submit" className="modal-submit-btn-premium" disabled={loading || availabilityStatus === 'booked'}>
                                    {loading ? 'Sending...' : <>Send Enquiry <Send size={16} /></>}
                                </button>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CheckAvailabilityModal;
